import React, { useState, useCallback, useEffect } from "react";
import { Clock, RotateCcw, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TelemetryTimestampSource } from "@/hooks/useTelemetryTimestamp";

/**
 * VideoTelemetrySyncOffset Component
 *
 * Synchronizes video and telemetry data based on their timestamps with fine-tuning capability.
 *
 * How it works:
 * 1. Video and telemetry files contain timestamp information (video: seconds precision, telemetry: minute precision)
 * 2. Auto-sync calculates initial offset: (video_timestamp - telemetry_timestamp)
 * 3. Users can fine-tune with ±60 second range in 0.1s increments
 * 4. Positive offset = video ahead of telemetry, Negative offset = telemetry ahead of video
 * 5. Sync is applied: telemetry_display_time = video_time + sync_offset
 */

interface VideoTelemetrySyncOffsetProps {
  syncOffset: number;
  onSyncOffsetChange: (offset: number) => void;
  videoCreatedAt?: string;
  telemetryStartTime?: string; // ISO string or readable format
  telemetryTimestampSource?: TelemetryTimestampSource; // Source of the telemetry timestamp
 className?: string;
}

export const VideoTelemetrySyncOffset: React.FC<
  VideoTelemetrySyncOffsetProps
> = ({
  syncOffset,
  onSyncOffsetChange,
  videoCreatedAt,
  telemetryStartTime,
  telemetryTimestampSource = "session_database",
  className = "",
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const [tempOffset, setTempOffset] = useState(syncOffset.toString());
  const [isEditing, setIsEditing] = useState(false);

  // Sync internal state with prop changes when not editing
  useEffect(() => {
    if (!isEditing) {
      setTempOffset(syncOffset.toString());
    }
  }, [syncOffset, isEditing]);

  const handleOffsetChange = useCallback(
    (value: string) => {
      setTempOffset(value);

      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        onSyncOffsetChange(numericValue);
      }
    },
    [onSyncOffsetChange],
  );

  const handleReset = useCallback(() => {
    const resetValue = 0;
    setTempOffset(resetValue.toString());
    onSyncOffsetChange(resetValue);
  }, [onSyncOffsetChange]);

  const handleFocus = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    // Ensure the display shows the actual sync offset value
    setTempOffset(syncOffset.toString());
  }, [syncOffset]);

  const formatTime = (seconds: number): string => {
    const absSeconds = Math.abs(seconds);
    const sign = seconds < 0 ? "-" : "+";

    if (absSeconds < 60) {
      return `${sign}${absSeconds.toFixed(1)}s`;
    }

    const minutes = Math.floor(absSeconds / 60);
    const remainingSeconds = absSeconds % 60;
    return `${sign}${minutes}:${remainingSeconds.toFixed(1).padStart(4, "0")}`;
  };

  const formatTimestamp = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString([], {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return t("pages:sessions.sync.unknown");
    }
  };

  const formatFullTimestamp = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString([], {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return t("pages:sessions.sync.unknown");
    }
  };

  // Calculate automatic sync suggestion based on timestamps
  const getAutoSyncOffset = (): number | null => {
    if (!videoCreatedAt || !telemetryStartTime) return null;

    try {
      const videoTime = new Date(videoCreatedAt).getTime();
      const telemetryTime = new Date(telemetryStartTime).getTime();
      return (videoTime - telemetryTime) / 1000; // Convert to seconds
    } catch {
      return null;
    }
  };

  const autoSyncOffset = getAutoSyncOffset();

  const getTimestampSourceLabel = (source: string): string => {
    switch (source) {
      case "telemetry_metadata":
        return t("pages:sessions.sync.sourceTelemetryMetadata");
      case "telemetry_summary":
        return t("pages:sessions.sync.sourceTelemetrySummary");
      case "session_database":
        return t("pages:sessions.sync.sourceSessionDatabase");
      default:
        return t("pages:sessions.sync.unknownSource");
    }
  };

  const getTimestampSourceColor = (source: string): string => {
    switch (source) {
      case "telemetry_metadata":
      case "telemetry_summary":
        return "text-[#4ade80]";
      case "session_database":
        return "text-[#facc15]";
      default:
        return "text-[#94a3b8]";
    }
  };

  return (
    <div
 className={`bg-[#0b1220] border border-[#1f2937] rounded-lg p-3 ${className}`}
    >
 <div className="flex items-center justify-between mb-3">
 <div className="flex items-center space-x-2">
 <Clock className="h-4 w-4 text-[#60a5fa]" />
          <span className="text-sm font-medium text-slate-200">
            {t("pages:sessions.sync.title")}
          </span>
        </div>
        <button
          onClick={handleReset}
          className="p-1 text-[#64748b] hover:text-slate-200 hover:bg-[#111827] rounded transition-colors"
          title={t("pages:sessions.sync.resetOffset")}
        >
 <RotateCcw className="h-3 w-3" />
        </button>
      </div>

      {/* Timestamp Information */}
      {(videoCreatedAt || telemetryStartTime) && (
 <div className="mb-3 text-xs text-[#94a3b8] space-y-2">
          <div className="text-xs font-medium text-slate-300 mb-1">
            {t("pages:sessions.sync.startTimestamps")}
          </div>
          {videoCreatedAt && (
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-[#94a3b8]">{t("pages:sessions.sync.videoStart")}</span>
                <span className="font-mono text-slate-200">
                  {formatTimestamp(videoCreatedAt)}
                </span>
              </div>
              <div className="text-xs text-[#64748b] text-right font-mono">
                {formatFullTimestamp(videoCreatedAt)}
              </div>
            </div>
          )}
          {telemetryStartTime && (
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-[#94a3b8]">{t("pages:sessions.sync.telemetryStart")}</span>
 <span className="font-mono text-slate-200">
                  {formatTimestamp(telemetryStartTime)}
                </span>
              </div>
 <div className="text-xs text-[#64748b] text-right font-mono">
                {formatFullTimestamp(telemetryStartTime)}
              </div>
            </div>
          )}
          {videoCreatedAt && telemetryStartTime && (
 <div className="pt-1 border-t border-[#1f2937]">
 <div className="flex justify-between text-xs">
              <span className="text-[#94a3b8]">{t("pages:sessions.sync.timeDifference")}</span>
 <span className="font-mono text-slate-200">
                  {formatTime(getAutoSyncOffset() || 0)}
                </span>
              </div>
            </div>
          )}
          {telemetryStartTime && (
 <div className="pt-1 mt-1 border-t border-[#1f2937]">
 <div className="flex justify-between text-xs">
              <span className="text-[#64748b]">{t("pages:sessions.sync.telemetrySource")}</span>
                <span
 className={`text-xs ${getTimestampSourceColor(telemetryTimestampSource)}`}
                >
                  {getTimestampSourceLabel(telemetryTimestampSource)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Auto-sync suggestion */}
      {autoSyncOffset !== null && Math.abs(autoSyncOffset - syncOffset) > 1 && (
 <div className="mb-3 p-2 bg-[#0f172a] border border-[#1e3a8a] rounded text-xs">
 <div className="flex items-center space-x-1 mb-1">
 <AlertCircle className="h-3 w-3 text-[#60a5fa]" />
          <span className="text-[#93c5fd] font-medium">
              {t("pages:sessions.sync.autoSyncAvailable")}
            </span>
          </div>
          <div className="text-xs text-[#bfdbfe] mb-2">
            {t("pages:sessions.sync.suggestedOffsetDesc")}
            <span className="font-mono font-bold">
              {formatTime(autoSyncOffset)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[#60a5fa] text-xs">
              {autoSyncOffset > 0
                ? t("pages:sessions.sync.videoStartedLater")
                : t("pages:sessions.sync.telemetryStartedLater")}
            </span>
            <button
              onClick={() => handleOffsetChange(autoSyncOffset.toString())}
              className="px-2 py-1 bg-[#2563eb] text-white text-xs rounded hover:bg-[#1d4ed8] transition-colors"
            >
              {t("pages:sessions.sync.applyAutoSync")}
            </button>
          </div>
        </div>
      )}

      {/* Manual Offset Control */}
 <div className="space-y-2">
 <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-slate-300">
            {t("pages:sessions.sync.syncOffset")}
          </label>
          <span className="text-xs text-[#94a3b8]">
            {syncOffset > 0
              ? t("pages:sessions.sync.videoAhead")
              : syncOffset < 0
                ? t("pages:sessions.sync.telemetryAhead")
                : t("pages:sessions.sync.aligned")}
          </span>
        </div>

 <div className="flex items-center space-x-2">
          <input
            type="range"
            min="-60"
            max="60"
            step="0.1"
            value={syncOffset}
            onChange={(e) => handleOffsetChange(e.target.value)}
 className="flex-1 h-1 bg-[#1f2937] rounded-lg appearance-none cursor-pointer"
          />
          <input
            type="number"
            value={isEditing ? tempOffset : syncOffset.toFixed(1)}
            onChange={(e) => handleOffsetChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            step="0.1"
 className="w-16 px-2 py-1 text-xs border border-[#334155] bg-[#020617] text-slate-200 rounded text-center"
          />
 <span className="text-xs text-[#94a3b8] w-6">s</span>
        </div>

 <div className="text-center">
 <span className="text-xs font-mono text-slate-300">
            {formatTime(syncOffset)}
          </span>
        </div>
      </div>

      {/* Quick adjustment buttons */}
 <div className="flex justify-center space-x-1 mt-3">
        {[-10, -1, -0.1, 0.1, 1, 10].map((adjustment) => (
          <button
            key={adjustment}
            onClick={() =>
              handleOffsetChange((syncOffset + adjustment).toString())
            }
 className="px-2 py-1 text-xs bg-[#111827] hover:bg-[#1f2937] rounded text-slate-300 border border-[#1f2937] transition-colors"
          >
            {adjustment > 0 ? "+" : ""}
            {adjustment}
          </button>
        ))}
      </div>
    </div>
  );
};
